// @vendors
import React, { useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

// @components
import Button from "../../../../components/atoms/Button";

// @types
import { ServiceRequest } from "../../../../types/request.types";

// @store
import useProposalStore from "../../../../store/useProposalStore";
import { useSpinnerStore } from "../../../../store/useSpinnerStore";
import { useCompanyStore } from "../../../../store/useCompanyStore";
import useServiceRequestStore from '../../../../store/useServiceRequestStore';

interface ProposalFormProps {
  selectedRequest: ServiceRequest | null;
  handleResetRequestSelect: () => void;
}

export const ProposalForm: React.FC<ProposalFormProps> = ({ selectedRequest, handleResetRequestSelect }) => {
  const { t } = useTranslation();
  const { data } = useCompanyStore();
  const { openSpinner } = useSpinnerStore();
  const { submitProposal, isLoading, error } = useProposalStore();
  const { getFilteredRequests } = useServiceRequestStore();
  const [formData, setFormData] = useState({
    proposalValue: 0,
    daysToComplete: 0,
    descriptionProposet: ''
  });

  if (!selectedRequest) {
    return (
      <div className="w-full p-4 bg-cBackground dark:bg-dark-cBackground xl:h-[75vh] rounded-lg shadow-lg text-gray-500 dark:text-dark-cText/60 flex items-center justify-center text-center">
        {t("professional.request.textThirCol")}
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    openSpinner(true);
    try {
      await submitProposal({
        serviceRequestId: selectedRequest.serviceRequestId,
        ...formData
      });
      return successProposal();
    } catch (error) {
      openSpinner(false);
      toast.error(t("professional.error.postData"));
    }
  };

  const successProposal = () => {
    getRequest();
    handleResetRequestSelect();
    setFormData({
      proposalValue: 0,
      daysToComplete: 0,
      descriptionProposet: ''
    });
    toast.success(t("professional.request.successPost"));
    openSpinner(false);
  }

  const getRequest = () => {
    if (!data?.category.categoryId) return;
    const params = {
      categoryId: data.category.categoryId,
      pageNumber: 1,
      pageSize: 100,
      applicatedByMe: false
    }
    getFilteredRequests(params);
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="w-full p-4 border rounded bg-cBackground dark:bg-dark-cBackground dark:border-none h-[80vh] xl:h-[75vh] overflow-hidden xl:overflow-auto"
    >
      <h3 className="font-bold mb-4 dark:text-dark-cText">{t("professional.request.getRequest")}</h3>

      <div className="mb-4">
        <label className="block mb-2 dark:text-dark-cText/70">
          {t("professional.request.valueRequest")}
        </label>
        <input
          type="number"
          name="proposalValue"
          value={formData.proposalValue}
          onChange={handleChange}
          className="w-full p-2 border rounded dark:bg-dark-cBackground dark:border-gray-700 dark:text-dark-cText/60"
          required
        />
      </div>

      <div className="mb-4">
        <label className="block mb-1 dark:text-dark-cText/70">
          {t("professional.request.daysToCompleted")}
        </label>
        <label className="block mb-1 text-[12px] text-gray-400 dark:text-dark-cText/70">
          {t("professional.request.daysToCompletedlabel")}
        </label>
        <input
          type="number"
          name="daysToComplete"
          value={formData.daysToComplete}
          onChange={handleChange}
          className="w-full p-2 border rounded dark:bg-dark-cBackground dark:border-gray-700 dark:text-dark-cText/60"
          required
        />
      </div>

      <div className="mb-4">
        <label className="block mb-2 dark:text-dark-cText/70">
          {t("professional.request.descriptionRequest")}
        </label>
        <label className="block mb-1 text-[12px] text-gray-400 dark:text-dark-cText/70">
          {t("professional.request.descriptionRequestlabel")}
        </label>
        <textarea
          name="descriptionProposet"
          value={formData.descriptionProposet}
          onChange={handleChange}
          className="w-full p-2 border rounded dark:bg-dark-cBackground dark:border-gray-700 dark:text-dark-cText/60"
          rows={4}
          required
        />
      </div>

      {error && <div className="text-red-500 mb-4">{error}</div>}

      <Button
        type="submit"
        disabled={isLoading}
      >
        {t("professional.request.buttonSend")}
      </Button>
    </form>
  );
};